import { makeAutoObservable, runInAction, toJS } from 'mobx';
import {
  ProductLineAttr,
  MarketingTag,
  Portfolio,
  RateSegment,
  CartItem,
  CartBrowsingCriteria,
  BrowsingInfo,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { RootStore } from 'store/rootStore';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { Cart } from 'types/cartService';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStore';
import { Catalog, PortfolioRequest } from 'types/productService';
import { ApolloQueryResult } from '@apollo/client';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { ToastInfo } from 'types/general';
import { getCatalog, getPortfoliosById, getPortfolios } from 'api/operations/productServiceV2';
import { combinePortfolios } from 'global/services-helpers/productService';

export type ComparisonPageType =
  | 'variants'
  | 'details'
  | 'portfolios'
  | 'cart'
  | 'proposal-details'
  | 'all-portfolios'
  | 'popular'
  | 'config-portfolio';

export interface ConfiguredPortfolioData {
  portfolioId: string;
  portfolioName: string;
  productLineId: ProductLineId;
}

export function isPageType(value: string): value is ComparisonPageType {
  return [
    'variants',
    'details',
    'portfolios',
    'all-portfolios',
    'cart',
    'proposal-details',
    'popular',
    'config-portfolio',
  ].includes(value as ComparisonPageType);
}

export class ComparisonStore {
  rootStore: RootStore;
  loading = false;
  /**
   * List of all product line attributes with their titles
   */
  productLineAttrs: ProductLineAttr[] = [];
  /**
   * List of all available marketing tags with their titles
   */
  marketingTags: MarketingTag[] = [];
  /**
   * Data of the Portfolios
   */
  portfolios: Portfolio[] = [];
  /**
   * Data of the filtered Portfolios
   */
  filteredPortfolios: Portfolio[] = [];
  /**
   * Current product line id
   */
  productLineId: ProductLineId | null = null;
  /**
   *  Determines in which portfolio heading show loader
   */
  loaderInPortfolioId = '';
  /**
   * Current Cart with items and browsing criteria
   */
  cart: Cart = INITIAL_CART_OBJECT;
  /**
   * Toast Info
   */
  toastInfo: ToastInfo = {
    showToast: false,
    toastType: ToastColorTypes.SUCCESS,
    toastMessage: '',
  };

  /**
   * This data pertains to the portfolio being configured in the portfolio config modal.
   * It is necessary for adding a portfolio browsing criteria version via the modal.
   */
  currentConfiguredPortfolioData: ConfiguredPortfolioData | null = null;

  // Setter section
  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  setProductLineId(productLineId: ProductLineId): void {
    this.productLineId = productLineId;
  }

  public showToast(toastMessage: string, type: ToastColorTypes): void {
    this.resetToastInfo();
    this.toastInfo.showToast = true;
    this.toastInfo.toastMessage = toastMessage;
    this.toastInfo.toastType = type;
  }

  public setConfiguredPortfolio(portfolioId: string, portfolioName: string, productLineId: ProductLineId): void {
    this.currentConfiguredPortfolioData = { portfolioId, portfolioName, productLineId };
  }

  public resetConfiguredPortfolio(): void {
    this.currentConfiguredPortfolioData = null;
  }

  public closeToast(): void {
    this.toastInfo.showToast = false;
    this.resetToastInfo();
  }

  private resetToastInfo(): void {
    this.toastInfo.toastType = ToastColorTypes.SUCCESS;
    this.toastInfo.toastMessage = '';
  }

  // Initial method tha collects all required info for the page
  async collectInitialData(
    cartId: string,
    productLineId: ProductLineId,
    type: ComparisonPageType,
    portfolioId: string | null,
    portfolioIds?: string,
  ): Promise<void> {
    this.setProductLineId(productLineId);
    const rateSegment = PRODUCT_LINES_CONTENT_MAP[productLineId].rateSegment;
    this.setLoading(true);
    const result = await this.rootStore.cartStore.getCart(cartId);
    await runInAction(async () => {
      const cartData = result.data.getCart;
      this.cart = cartData;
      const catalogResult = await this.getCatalog(
        cartData.lastUpdateBrowsingCriteria.effectiveDate,
        rateSegment,
        cartData.lastUpdateBrowsingCriteria.state,
      );
      // Show a link to the shopping cart in the header and provide the necessary data for redirection
      this.rootStore.domainStore.setDataForCartRedirect(cartId, cartData.groupId, true);
      this.rootStore.domainStore.changeCartItemsQuantity(cartData.cartItems.length);

      void runInAction(async () => {
        if (catalogResult) {
          const { marketingTags, productLineAttributes } = catalogResult.data.getCatalog;
          this.marketingTags = marketingTags;
          const label = PRODUCT_LINES_CONTENT_MAP[productLineId].joinedLabel;
          this.productLineAttrs = productLineAttributes.filter(
            (attribute) => attribute.productLine.replaceAll(' ', '') === label,
          );
        }

        await this.getPortfolios(
          result.data.getCart,
          {
            sponsorId: this.rootStore.domainStore.domainHeader.id,
            agencyId: '',
            rateSegment,
            productLineId,
            bc: result.data.getCart.lastUpdateBrowsingCriteria,
            groupId: result.data.getCart.groupId,
          },
          type,
          portfolioId,
          portfolioIds,
        );
      });
    });
  }

  async getCatalog(
    effectiveDate: string,
    rateSegment: RateSegment,
    state: string,
  ): Promise<ApolloQueryResult<{ getCatalog: Catalog }>> {
    return getCatalog(this.rootStore.domainStore.domainHeader.id, effectiveDate, rateSegment, state);
  }

  async getPortfolios(
    cart: Cart,
    request: PortfolioRequest,
    type: ComparisonPageType,
    portfolioId: string | null,
    portfolioIds?: string,
  ): Promise<void> {
    // make 2 requests for getting portfolios
    if (cart.cartItems.length > 0) {
      const resPortfoliosWithBc = await getPortfoliosById(
        cart.cartItems,
        type === 'proposal-details' || type === 'popular' ? undefined : cart.lastUpdateBrowsingCriteria,
      );
      void runInAction(async () => {
        const resPortfolios = await getPortfolios(request);
        runInAction(() => {
          const portfolios = resPortfolios.data.getPortfolios;

          const combinedPortfolios = combinePortfolios(portfolios, resPortfoliosWithBc);
          this.portfolios = combinedPortfolios;
          this.filterPortfoliosByType(cart, type, portfolioId, combinedPortfolios, portfolioIds || '');
          this.setLoading(false);
        });
      });
    } else {
      // make only 1 requests for getting portfolios.
      // It means that there are no versions added to cart and the 2nd request is unnecessary
      const resPortfolios = await getPortfolios(request);
      runInAction(() => {
        this.portfolios = resPortfolios.data.getPortfolios;
        this.filterPortfoliosByType(cart, type, portfolioId, resPortfolios.data.getPortfolios, portfolioIds || '');
        this.setLoading(false);
      });
    }
  }

  filterPortfoliosByType(
    cart: Cart,
    pageType: ComparisonPageType,
    portfolioId: string | null,
    portfolios: Portfolio[],
    portfolioIds?: string,
  ): void {
    const convertedPortfolios = portfolios.map((portfolio) => toJS(portfolio)).slice();

    switch (pageType) {
      case 'variants':
      case 'details':
      case 'popular':
      case 'config-portfolio':
      case 'proposal-details': {
        if (portfolioId) {
          const currentPortfolio = convertedPortfolios.find(({ id }) => portfolioId === id);
          if (currentPortfolio) this.filteredPortfolios = [currentPortfolio];
        }
        break;
      }
      case 'all-portfolios': {
        this.filteredPortfolios = convertedPortfolios.filter(
          ({ productLineId }) => productLineId === this.productLineId,
        );
        break;
      }
      case 'portfolios': {
        if (portfolioIds) {
          const portfolioIdsList = portfolioIds.split(',');
          const currentPortfolios = convertedPortfolios.filter(({ id }) => portfolioIdsList.includes(id));
          this.filteredPortfolios = currentPortfolios;
        }
        break;
      }
      case 'cart': {
        const currentPortfolios = convertedPortfolios.filter(({ id }) => {
          return cart.cartItems.find(
            (cartItem) => cartItem.portfolioId === id && this.productLineId === cartItem.productLineId,
          );
        });

        this.filteredPortfolios = currentPortfolios;
        break;
      }
      default: {
        break;
      }
    }
  }

  async onAddToProposal(
    portfolioId: string,
    bcIndex: number | null,
    portfolioName: string,
    productLineId: ProductLineId,
    /**
     * Updated preferences for a specific product line, intended to replace or expand upon the existing browsing criteria.
     */
    overrideBC?: BrowsingInfo,
    /**
     *  Determines whether the version is "general" or "additional," impacting the display of browsing cards on the browsing page.
     *  Browsing cards will only showcase updates to general(not additional) browsing criteria
     *  Additional browsing criteria will be applied on the proposal page to configure the portfolio.
     */
    additionalConfig = 'false',
  ): Promise<void> {
    try {
      /**
       * The currentCart signifies the cart containing cartItems prior to the addition of a new portfolio version.
       * Within cartItems of currentCart, modifications have been made, including the addition of a property that aids in discerning whether a version has been added to the cart or not.
       * This approach prevents the loss of versions that were removed on the comparison page before introducing a new version, as the removed versions would not be preserved in the cart otherwise.
       */
      const currentCart = toJS(this.cart);
      const { id, lastUpdateBrowsingCriteria } = currentCart;
      // Show spinner in portfolio heading with this id
      this.loaderInPortfolioId = portfolioId;
      const convertedPortfolios = this.filteredPortfolios.map((portfolio) => toJS(portfolio)).slice();
      const currentPortfolio = convertedPortfolios.find((portfolio) => portfolio.id === portfolioId);

      const bcVersion = currentPortfolio && Object.values(currentPortfolio.bcVersions)[bcIndex || 0];
      // Setting the value of additionalConfig is crucial for maintaining a clear separation between the general and additional configurations
      const browsingCriteria = { ...lastUpdateBrowsingCriteria, ...bcVersion, additionalConfig };
      if (overrideBC) {
        // Add additional browsing criteria for particular product line.
        browsingCriteria[productLineId] = {
          ...browsingCriteria[productLineId],
          ...overrideBC,
        };
      }

      const res = await this.rootStore.cartStore.addPortfolioToCart(
        {
          cartId: id,
          portfolioId,
          portfolioName,
          browsingCriteria,
          productLineId,
          bcIndex,
        },
        this.rootStore.domainStore.domainHeader.id,
      );
      void runInAction(() => {
        /**
         * The updatedCart represents the cart containing cartItems after the addition of a new version.
         * The cartItems within updatedCart do not possess the additional 'added' field.
         * Therefore, modifications are necessary to ensure the expected and seamless behavior of adding and removing versions while retaining them.
         */
        const updatedCart = res.data?.addPortfolioConfigToCart;
        if (updatedCart) {
          const addedCartItem = currentCart.cartItems.find((cartItem) => cartItem.portfolioId === portfolioId);
          // If a portfolio was added to the proposal on the comparison page and there is no existing cart item with the same portfolioId, the addedCartItem may be undefined.
          const newCartItem = updatedCart.cartItems.find((cartItem) => cartItem.portfolioId === portfolioId);

          // Browsing criteria for the newly added version.
          const lastUpdatedBC = bcIndex ? newCartItem?.browsingCriteria[bcIndex] : undefined;

          const updatedCartItems = this.modifyCartItems(
            'ADD',
            currentCart, // Utilize the currentCart instead of updatedCart to retain the modified "cartItems" containing the additional field.
            bcIndex || 0,
            addedCartItem || newCartItem,
            lastUpdatedBC,
          );
          this.cart = { ...updatedCart, cartItems: updatedCartItems };

          if (additionalConfig === 'true') {
            void this.updatePortfolioWithAdditionalConfig(newCartItem, currentPortfolio, bcIndex);
          }
        }
        // Hide spinner in portfolio heading
        this.loaderInPortfolioId = '';
      });
    } catch {
      this.loaderInPortfolioId = '';
    }
  }

  /**
   * Update the portfolio data and assign it to the filtered portfolios. Currently, this functionality is only implemented for a single portfolio configuration.
   *
   * @param newCartItem The cart item has been updated with the most recent data and includes versions
   * that were retrieved from the cart subsequent to the addition of a new portfolio version (not modified)
   * @param outdatedPortfolio The portfolio that needs to be updated to align with the new browsing criteria.
   *  Moreover, the information related to the associated products, encompassing rates and attributes, must also undergo updates.
   * @param bcIndex Index of the version to be updated.
   */
  async updatePortfolioWithAdditionalConfig(
    newCartItem: CartItem | undefined,
    outdatedPortfolio: Portfolio | undefined,
    bcIndex: number | null,
  ): Promise<void> {
    if (!newCartItem) return;
    // Retrieve the updated portfolio with accurate browsing criteria and products information.
    const result = await getPortfoliosById([newCartItem]);
    runInAction(() => {
      const updatedPortfolio = result[0];
      if (outdatedPortfolio) {
        const portfolio = { ...outdatedPortfolio };
        if (bcIndex) {
          // Update list of the all portfolio versions for particular version index
          portfolio.bcVersions[bcIndex] = updatedPortfolio.bcVersions[bcIndex];

          // Update Product info (rates, attributes) for new version
          portfolio.products.forEach((product) => {
            const currentProduct = updatedPortfolio.products.find(
              ({ imProductId }) => imProductId === product.imProductId,
            );
            if (!currentProduct) return;
            // eslint-disable-next-line no-param-reassign
            product.bcDependentData[bcIndex] = currentProduct.bcDependentData[bcIndex];
          });
          this.filteredPortfolios = [portfolio];
        }
      }
    });
  }

  async onRemoveFromProposal(portfolioId: string, bcIndex: number): Promise<void> {
    // Show spinner in portfolio heading with this id
    this.loaderInPortfolioId = portfolioId;
    const convertedCart = toJS(this.cart);
    const removedCartItem = convertedCart.cartItems.find((cartItem) => cartItem.portfolioId === portfolioId);
    const res = await this.rootStore.cartStore.removeBcByIndex(convertedCart.id, portfolioId, bcIndex);

    runInAction((): void => {
      const currentCart = res.data?.removeBCByIndex;
      if (currentCart) {
        // Introduce or update a new property in the cartItem to accurately indicate the selected status of the comparison header card.
        const updatedCartItems = this.modifyCartItems('REMOVE', convertedCart, bcIndex, removedCartItem);
        this.cart = { ...currentCart, cartItems: updatedCartItems };
        this.rootStore.domainStore.changeCartItemsQuantity(currentCart.cartItems.length);
      }
      // Hide spinner in portfolio heading
      this.loaderInPortfolioId = '';
    });
  }

  /**
   * Update the 'added' property within the cart item's browsing criteria to enable removal while simultaneously displaying it on the page.
   * This provides the capability to add it again within the same page context.
   * Furthermore, if the browsing criteria version does not exist in the cartItem, it generates a new version for it.
   *
   * @param type Type of the action, it indicates the impending process and influences the actions that need to be undertaken.
   * @param cart Current cart with modified cartItems (with additional field)
   * @param bcIndex Index of the version to be updated.
   * @param updatedCartItem Cart item within which changes will occur.
   * @param updatedCartItemBc An optional parameter exclusively intended for the ADD action, defining the browsing criteria for the newly added version.
   * @returns The list of modified cartItems with additional field and created versions
   */
  // eslint-disable-next-line class-methods-use-this
  modifyCartItems(
    type: 'ADD' | 'REMOVE',
    cart: Cart,
    bcIndex: number,
    updatedCartItem: CartItem | undefined,
    updatedCartItemBc?: CartBrowsingCriteria,
  ): CartItem[] {
    const currentCart = { ...cart };
    const currentCartItem = updatedCartItem && { ...updatedCartItem };

    // If a portfolio was added on the comparison page and wasn't in the cart yet, the cart item may not be present in the cart.
    // The response after Add to proposal/Remove from proposal requests is not set to store to save the value of the 'added' property in browsingCriteria (within the cartItem).
    // The 'added' property on the comparison page indicates whether the portfolio is added to the cart.
    const isCartItemInCart = cart.cartItems.find((cartItem) => cartItem.portfolioId === currentCartItem?.portfolioId);

    if (!isCartItemInCart && currentCartItem && type === 'ADD') {
      currentCart.cartItems.push(currentCartItem);
    }

    const updatedCartItems = currentCart.cartItems.map((cartItem) => {
      if (cartItem.portfolioId === currentCartItem?.portfolioId && currentCartItem) {
        // If a portfolio has not been added on the browsing page, there may not be a corresponding version of it in the cart.
        if (!currentCartItem.browsingCriteria[bcIndex] && !updatedCartItemBc) {
          currentCartItem.browsingCriteria[bcIndex] = {} as CartBrowsingCriteria;
        }
        // Create a new version in the cartItem. Configure options are already in the updatedCartItemBc
        if (!currentCartItem.browsingCriteria[bcIndex] && updatedCartItemBc) {
          currentCartItem.browsingCriteria[bcIndex] = {
            ...currentCart.lastUpdateBrowsingCriteria,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            ...currentCartItem.browsingCriteria[bcIndex],
            ...updatedCartItemBc,
          } as CartBrowsingCriteria;
        }
        // Set a flag that assists in determining whether a version has been added to the cart and enables visual representation accordingly.
        currentCartItem.browsingCriteria[bcIndex].added = type === 'ADD';
        return { ...currentCartItem, browsingCriteria: currentCartItem.browsingCriteria };
      }
      return cartItem;
    });
    return updatedCartItems;
  }

  /**
   * Determine or compute an index for inserting a version with additional configurations,
   * along with the "additionalConfig" flag that indicates whether this version pertains to general or additional browsing criteria.
   *
   * @param bcByProductLine The browsing criteria specific to the product line, aiding in locating the index of the existing version or
   * generating a new version if none exists with these configurations.
   * @param cartItems The list of modified cart items (with additional field)
   * @param currentPortfolioId The ID of the portfolio to which the new version will be added.d
   * @returns The index of the existing or new browsing criteria version, along with a flag indicating whether this version is classified as general or additional.
   */
  // eslint-disable-next-line class-methods-use-this
  public findMatchingVersionIndex(
    bcByProductLine: BrowsingInfo | undefined,
    cartItems: CartItem[],
    currentPortfolioId: string,
  ): { bcIndex: number; additionalConfig: string } {
    const currentCartItem = cartItems.find((item) => item.portfolioId === currentPortfolioId);

    if (!currentCartItem) {
      return { bcIndex: 0, additionalConfig: 'true' }; // Return version 0 as the default version
    }

    let matchingVersion: number | null = null;

    // eslint-disable-next-line no-restricted-syntax
    for (const [browsingCriteriaKey, browsingInfo] of Object.entries(currentCartItem.browsingCriteria)) {
      if (browsingInfo && bcByProductLine && browsingInfo[currentCartItem.productLineId]) {
        const productLineInfo = browsingInfo[currentCartItem.productLineId];

        const isSame = productLineInfo
          ? Object.keys(bcByProductLine).every((key) => productLineInfo[key] === bcByProductLine[key])
          : false;

        if (isSame) {
          matchingVersion = +browsingCriteriaKey;
          break; // Break the loop if a matching version with isSame === true is found
        }
      }
    }

    if (matchingVersion !== null) {
      return {
        bcIndex: matchingVersion,
        additionalConfig: currentCartItem.browsingCriteria[matchingVersion]?.additionalConfig?.toString() || 'false',
      };
    }

    // If a matching version is not located, determine the most recent version number and provide the subsequent version number as the result.
    const lastVersion = Math.max(...Object.keys(currentCartItem.browsingCriteria).map(Number));
    return { bcIndex: lastVersion + 1, additionalConfig: 'true' };
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
  }
}
